// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/components/FacultyProfileModal.js

import React, { useEffect } from 'react';
import { 
  FiX as CloseIcon, 
  FiMail as MailIcon, 
  FiPhone as PhoneIcon, 
  FiAward as AwardIcon,
  FiBookOpen as BookIcon,
  FiBriefcase as BriefcaseIcon,
  FiGlobe as GlobeIcon
} from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

const FacultyProfileModal = ({ member, onClose = () => {}, apiBaseURL = '' }) => {
  const { primary, secondary, primaryLight, secondaryLight, fontBody, fontHeading } = useBrandColors();

  // Key handler
  useEffect(() => {
    if (!member) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [member, onClose]);

  if (!member) return null;

  const img = buildResolvedFile(
    member.image || member.photo || member.imageUrl || '',
    apiBaseURL
  ).resolvedSrc;

  const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim() || '—';

  // Get initials for fallback
  const getInitials = () => {
    const name = fullName;
    if (name === '—') return 'F';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        style={{ fontFamily: fontBody }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl z-10"
          style={{ borderBottomColor: `${primary}15` }}
        >
          <h3 
            className="text-xl font-bold"
            style={{ 
              color: primary,
              fontFamily: fontHeading,
            }}
          >
            Faculty Profile
          </h3>
          <button
            onClick={onClose}
            aria-label="Close profile"
            className="p-2 rounded-lg transition-colors"
            style={{ color: '#6B7280' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${primary}10`}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Profile Header */}
          <div className="flex flex-col md:flex-row gap-6">
            {/* Image */}
            <div className="flex-shrink-0">
              <div 
                className="w-32 h-32 rounded-2xl overflow-hidden shadow-sm border"
                style={{ 
                  backgroundColor: primaryLight,
                  borderColor: `${primary}15`
                }}
              >
                {img ? (
                  <img
                    src={img}
                    alt={fullName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center"
                    style={{ 
                      background: `linear-gradient(135deg, ${primaryLight}, ${secondaryLight})`
                    }}
                  >
                    <span 
                      className="text-3xl font-bold"
                      style={{ color: secondary }}
                    >
                      {getInitials()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h4 
                className="text-2xl font-bold"
                style={{ 
                  color: '#1F2937',
                  fontFamily: fontHeading,
                }}
              >
                {fullName}
              </h4>
              <p 
                className="text-sm font-medium mt-1"
                style={{ color: primary }}
              >
                {member.designation || 'Faculty'}
              </p>
              {member.department && (
                <div 
                  className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-medium"
                  style={{ 
                    backgroundColor: `${secondary}15`,
                    color: secondary,
                  }}
                >
                  {member.department}
                </div>
              )}

              {/* Contact */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {member.email && (
                  <div className="flex items-center gap-3" style={{ color: '#6B7280' }}>
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${primary}10` }}
                    >
                      <MailIcon className="w-4 h-4" style={{ color: secondary }} />
                    </div>
                    <span className="text-sm break-words">{member.email}</span>
                  </div>
                )}

                {member.phone && (
                  <div className="flex items-center gap-3" style={{ color: '#6B7280' }}>
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${primary}10` }}
                    >
                      <PhoneIcon className="w-4 h-4" style={{ color: secondary }} />
                    </div>
                    <span className="text-sm break-words">{member.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Experience */}
          {member.experience && (
            <div 
              className="rounded-xl p-5"
              style={{ backgroundColor: `${primary}05` }}
            >
              <div className="flex items-center gap-2 mb-2">
                <BriefcaseIcon className="w-4 h-4" style={{ color: secondary }} />
                <h5 
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: '#6B7280' }}
                >
                  Professional Experience
                </h5>
              </div>
              <div className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                {member.experience}
              </div>
            </div>
          )}

          {/* Qualifications */}
          {member.qualifications && Array.isArray(member.qualifications) && member.qualifications.length > 0 && (
            <div 
              className="rounded-xl p-5 border"
              style={{ borderColor: `${primary}15` }}
            >
              <div className="flex items-center gap-2 mb-2">
                <AwardIcon className="w-4 h-4" style={{ color: secondary }} />
                <h5 
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: '#6B7280' }}
                >
                  Qualifications
                </h5>
              </div>
              <div className="flex flex-wrap gap-2">
                {member.qualifications.map((qual, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: `${primary}10`,
                      color: primary,
                    }}
                  >
                    {qual}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Research Interests */}
          {member.researchInterests && Array.isArray(member.researchInterests) && member.researchInterests.length > 0 && (
            <div 
              className="rounded-xl p-5 border"
              style={{ borderColor: `${primary}15` }}
            >
              <div className="flex items-center gap-2 mb-2">
                <BookIcon className="w-4 h-4" style={{ color: secondary }} />
                <h5 
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: '#6B7280' }}
                >
                  Research Interests
                </h5>
              </div>
              <div className="flex flex-wrap gap-2">
                {member.researchInterests.map((interest, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full text-xs"
                    style={{ 
                      backgroundColor: `${secondary}15`,
                      color: secondary,
                    }}
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Bio */}
          {member.bio && (
            <div 
              className="rounded-xl p-5 border"
              style={{ borderColor: `${primary}15` }}
            >
              <h5 
                className="text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: '#6B7280' }}
              >
                About
              </h5>
              <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                {member.bio}
              </p>
            </div>
          )}

          {/* Professional Profiles */}
          {(member.googleScholarId || member.linkedinId || member.orcidId) && (
            <div 
              className="rounded-xl p-5 border"
              style={{ borderColor: `${primary}15` }}
            >
              <div className="flex items-center gap-2 mb-3">
                <GlobeIcon className="w-4 h-4" style={{ color: secondary }} />
                <h5 
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: '#6B7280' }}
                >
                  Professional Profiles
                </h5>
              </div>

              {member.googleScholarId && (
                <div className="mb-2 last:mb-0">
                  <div 
                    className="text-[0.625rem] font-medium uppercase tracking-wider"
                    style={{ color: '#9CA3AF' }}
                  >
                    Google Scholar
                  </div>
                  <div className="text-sm font-mono text-xs break-words" style={{ color: '#6B7280' }}>
                    {member.googleScholarId}
                  </div>
                </div>
              )}

              {member.linkedinId && (
                <div className="mb-2 last:mb-0">
                  <div 
                    className="text-[0.625rem] font-medium uppercase tracking-wider"
                    style={{ color: '#9CA3AF' }}
                  >
                    LinkedIn
                  </div>
                  <div className="text-sm font-mono text-xs break-words" style={{ color: '#6B7280' }}>
                    {member.linkedinId}
                  </div>
                </div>
              )}

              {member.orcidId && (
                <div>
                  <div 
                    className="text-[0.625rem] font-medium uppercase tracking-wider"
                    style={{ color: '#9CA3AF' }}
                  >
                    ORCiD
                  </div>
                  <div className="text-sm font-mono text-xs break-words" style={{ color: '#6B7280' }}>
                    {member.orcidId}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Publications */}
          {member.publications > 0 && (
            <div 
              className="rounded-xl p-5"
              style={{ 
                backgroundColor: `${primary}05`,
                borderColor: `${primary}15`,
              }}
            >
              <div className="flex items-center gap-2">
                <BookIcon className="w-4 h-4" style={{ color: secondary }} />
                <span className="text-sm font-medium" style={{ color: primary }}>
                  {member.publications} {member.publications === 1 ? 'Publication' : 'Publications'}
                </span>
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-2">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
              style={{ 
                backgroundColor: `${primary}10`,
                color: primary,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${primary}20`}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${primary}10`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacultyProfileModal;